import { useSearchParams } from "react-router-dom";
import { CabeceraPagina } from "../../components/Armazon";
import { Aviso, Boton, Campo, Cargando, Tarjeta } from "../../components/ui";
import { SelectorCliente } from "../../components/SelectorCliente";
import { api } from "../../lib/api";
import { useDatos } from "../../lib/sesion";
import { ROL_ETIQUETA, TIPO_VISITA_ETIQUETA } from "../../lib/formato";
import type { Rol } from "../../lib/tipos";

interface Metrics {
  performed: number;
  notPerformed: number;
  cancelled: number;
  cancelledAfterAuthorization: number;
  cancelledBeforeAuthorization: number;
  rejected: number;
  managerCancelled: number;
  managerRejected: number;
  lastMinute: number;
  successes: number;
  nonSuccesses: number;
  noAnswer: number;
  inFollowup: number;
  teamAverage: number | null;
  clientAverage: number | null;
  performanceRate: number | null;
  successRate: number | null;
}
interface Stats {
  summary: Metrics;
  months: Array<{
    month: string;
    performed: number;
    notPerformed: number;
    cancelled: number;
  }>;
  salespeople: Array<Metrics & { id: string; name: string }>;
  byType: Array<Metrics & { value: string | null }>;
  byVenue: Array<Metrics & { value: string | null }>;
  cancellations: Array<{
    actorRole: Rol | null;
    actorId: string | null;
    name: string;
    before: number;
    after: number;
  }>;
  lastMinuteReasons: Array<{
    reason: string;
    rescheduled: boolean;
    count: number;
  }>;
}
const number = (n: number | null) =>
  n === null
    ? "—"
    : new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
const percent = (n: number | null) => (n === null ? "—" : number(n) + " %");
const LUGAR_ETIQUETA: Record<string, string> = {
  CLIENT: "Cliente",
  SP_HQ: "Sede Salvador Pinar",
  OTHER: "Otro",
};
const reasons: Record<string, string> = {
  CLIENT_CANCELLED: "El cliente canceló",
  CLIENT_ABSENT: "El cliente no se presentó",
  TEAM_UNAVAILABLE: "Imposibilidad del equipo",
  LOGISTICS: "Logística",
  FORCE_MAJEURE: "Causa mayor",
  OTHER: "Otro",
  CANCELLED: "Cancelación de visita autorizada",
};
export function Estadisticas() {
  const [params, setParams] = useSearchParams();
  const update = (key: string, value: string) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  const allowed = [
    "from",
    "to",
    "salespersonId",
    "clientId",
    "visitType",
    "venueType",
    "sort",
    "direction",
  ];
  const query = new URLSearchParams(
    [...params].filter(([k]) => allowed.includes(k)),
  ).toString();
  const { datos, cargando, error } = useDatos(
    (s) => api.get<Stats>("/admin/stats?" + query, s),
    [query],
  );
  const owners = useDatos(
    (s) =>
      api.get<{ rows: Array<{ id: string; name: string; role: Rol }> }>(
        "/admin/users",
        s,
      ),
    [],
  );
  const sort = (key: string) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set("sort", key);
      next.set(
        "direction",
        p.get("sort") === key && p.get("direction") !== "desc" ? "desc" : "asc",
      );
      return next;
    });
  const dateInput = (key: string, label: string) => (
    <Campo etiqueta={label}>
      <input
        type="date"
        value={params.get(key) ? localDate(params.get(key)!) : ""}
        onChange={(e) =>
          update(key, e.target.value ? madridDate(e.target.value) : "")
        }
      />
    </Campo>
  );
  function results(
    groups: Stats["byType"],
    labels: Record<string, string>,
    title: string,
  ) {
    return (
      <Tarjeta titulo={title}>
        <div className="tabla-scroll">
          <table>
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Éxitos</th>
                <th>No éxitos</th>
                <th>Sin respuesta</th>
                <th>Tasa de éxito</th>
                <th>En seguimiento</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr key={g.value ?? "null"}>
                  <td>
                    {g.value
                      ? (labels[g.value] ?? g.value)
                      : "Sin informar (anteriores)"}
                  </td>
                  <td>{g.successes}</td>
                  <td>{g.nonSuccesses}</td>
                  <td>{g.noAnswer}</td>
                  <td>{percent(g.successRate)}</td>
                  <td>{g.inFollowup}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Tarjeta>
    );
  }
  return (
    <>
      <CabeceraPagina
        titulo="Estadísticas"
        acciones={
          <a className="boton" href={"/api/admin/stats/export?" + query}>
            Exportar CSV
          </a>
        }
      />
      <div className="pila">
        <Tarjeta titulo="Filtros">
          <div className="rejilla tres">
            {dateInput("from", "Desde (incluido)")}
            {dateInput("to", "Hasta (excluido)")}
            <Campo etiqueta="Comercial">
              <select
                value={params.get("salespersonId") ?? ""}
                onChange={(e) => update("salespersonId", e.target.value)}
              >
                <option value="">Todos</option>
                {owners.datos?.rows
                  .filter(
                    (u) =>
                      u.role === "SALESPERSON" ||
                      datos?.salespeople.some((o) => o.id === u.id),
                  )
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
            </Campo>
            <Campo etiqueta="Tipo de visita">
              <select
                value={params.get("visitType") ?? ""}
                onChange={(e) => update("visitType", e.target.value)}
              >
                <option value="">Todos</option>
                {Object.entries(TIPO_VISITA_ETIQUETA).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta="Lugar">
              <select
                value={params.get("venueType") ?? ""}
                onChange={(e) => update("venueType", e.target.value)}
              >
                <option value="">Todos</option>
                {Object.entries(LUGAR_ETIQUETA).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Campo>
            <div>
              {params.has("clientId") ? (
                <div>
                  Cliente: {params.get("clientName") ?? "seleccionado"}{" "}
                  <Boton
                    onClick={() =>
                      setParams((p) => {
                        const n = new URLSearchParams(p);
                        n.delete("clientId");
                        n.delete("clientName");
                        return n;
                      })
                    }
                  >
                    Todos los clientes
                  </Boton>
                </div>
              ) : (
                <details>
                  <summary>Filtrar por cliente</summary>
                  <SelectorCliente
                    onSeleccion={(c) =>
                      setParams((p) => {
                        const n = new URLSearchParams(p);
                        n.set("clientId", c.id);
                        n.set("clientName", c.commercialName);
                        return n;
                      })
                    }
                  />
                </details>
              )}
            </div>
          </div>
          <Boton onClick={() => setParams({})}>Limpiar filtros</Boton>
        </Tarjeta>
        {error && <Aviso tono="error">{error}</Aviso>}
        {cargando && <Cargando />}
        {datos && (
          <>
            <div className="rejilla tres">
              {(
                [
                  ["Llevadas a cabo", datos.summary.performed],
                  ["No realizadas", datos.summary.notPerformed],
                  ["Canceladas", datos.summary.cancelled],
                  ["Último momento", datos.summary.lastMinute],
                  [
                    "Tasa de realización",
                    percent(datos.summary.performanceRate),
                  ],
                  [
                    "Nota media equipo / cliente",
                    number(datos.summary.teamAverage) +
                      " / " +
                      number(datos.summary.clientAverage),
                  ],
                  ["Éxitos", datos.summary.successes],
                  [
                    "No éxitos (incluye sin respuesta)",
                    datos.summary.nonSuccesses,
                  ],
                  ["Sin respuesta", datos.summary.noAnswer],
                  ["Tasa de éxito", percent(datos.summary.successRate)],
                  ["En valoración o seguimiento", datos.summary.inFollowup],
                  [
                    "Jefe: cancelaciones / rechazos",
                    datos.summary.managerCancelled +
                      " / " +
                      datos.summary.managerRejected,
                  ],
                ] as const
              ).map(([title, value]) => (
                <Tarjeta key={title} titulo={title}>
                  <strong className="estadistica-cifra">{value}</strong>
                </Tarjeta>
              ))}
            </div>
            <Tarjeta titulo="Visitas por mes">
              <div className="estadisticas-leyenda">
                <span data-serie="realizadas">Realizadas</span>
                <span data-serie="no-realizadas">No realizadas</span>
                <span data-serie="canceladas">Canceladas</span>
              </div>
              {datos.months.length === 0 && <p>Sin datos con estos filtros.</p>}
              {datos.months.map((m) => {
                const max = Math.max(
                  1,
                  ...datos.months.flatMap((x) => [
                    x.performed,
                    x.notPerformed,
                    x.cancelled,
                  ]),
                );
                return (
                  <div key={m.month} className="estadisticas-mes">
                    <strong>{m.month}</strong>
                    {(
                      [
                        ["realizadas", m.performed, "Realizadas"],
                        ["no-realizadas", m.notPerformed, "No realizadas"],
                        ["canceladas", m.cancelled, "Canceladas"],
                      ] as const
                    ).map(([serie, value, label]) => (
                      <div
                        key={serie}
                        className="estadisticas-barra"
                        aria-label={label + ": " + value}
                      >
                        <span
                          data-serie={serie}
                          style={{ width: (100 * value) / max + "%" }}
                        />
                        <b>{value}</b>
                      </div>
                    ))}
                  </div>
                );
              })}
            </Tarjeta>
            <Tarjeta titulo="Por comercial">
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      {[
                        ["name", "Comercial"],
                        ["performed", "Realizadas"],
                        ["notPerformed", "No realizadas"],
                        ["cancelled", "Canceladas"],
                        ["teamAverage", "Nota equipo"],
                        ["clientAverage", "Nota cliente"],
                        ["successRate", "Éxito (%)"],
                      ].map(([k, v]) => (
                        <th
                          key={k}
                          aria-sort={
                            params.get("sort") === k
                              ? params.get("direction") === "desc"
                                ? "descending"
                                : "ascending"
                              : "none"
                          }
                        >
                          <button onClick={() => sort(k!)}>{v}</button>
                        </th>
                      ))}
                      <th>Éxitos</th>
                      <th>No éxitos</th>
                      <th>Sin respuesta</th>
                      <th>Seguimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.salespeople.map((o) => (
                      <tr key={o.id}>
                        <td>{o.name}</td>
                        <td>{o.performed}</td>
                        <td>{o.notPerformed}</td>
                        <td>{o.cancelled}</td>
                        <td>{number(o.teamAverage)}</td>
                        <td>{number(o.clientAverage)}</td>
                        <td>{percent(o.successRate)}</td>
                        <td>{o.successes}</td>
                        <td>{o.nonSuccesses}</td>
                        <td>{o.noAnswer}</td>
                        <td>{o.inFollowup}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Tarjeta>
            {results(
              datos.byType,
              TIPO_VISITA_ETIQUETA,
              "Pedidos por tipo de visita",
            )}
            {results(datos.byVenue, LUGAR_ETIQUETA, "Pedidos por lugar")}
            <Tarjeta titulo="Cancelaciones por autor">
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Rol al actuar</th>
                      <th>Usuario</th>
                      <th>Antes de autorizar</th>
                      <th>Después de autorizar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.cancellations.map((c, i) => (
                      <tr key={i}>
                        <td>
                          {c.actorRole ? ROL_ETIQUETA[c.actorRole] : "Sistema"}
                        </td>
                        <td>{c.name}</td>
                        <td>{c.before}</td>
                        <td>{c.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Tarjeta>
            <Tarjeta titulo="Último momento: motivos">
              <div className="tabla-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Motivo</th>
                      <th>Resultado</th>
                      <th>Número</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.lastMinuteReasons.map((r, i) => (
                      <tr key={i}>
                        <td>{reasons[r.reason] ?? r.reason}</td>
                        <td>{r.rescheduled ? "Reprogramada" : "Definitiva"}</td>
                        <td>{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Tarjeta>
          </>
        )}
      </div>
    </>
  );
}
function localDate(iso: string) {
  if (!Number.isFinite(Date.parse(iso))) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}
function madridDate(day: string) {
  const utc = new Date(day + "T00:00:00Z");
  const offset = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Madrid",
    timeZoneName: "shortOffset",
  })
    .formatToParts(utc)
    .find((p) => p.type === "timeZoneName")!.value;
  return new Date(
    utc.getTime() - Number(offset.replace("GMT", "")) * 3600000,
  ).toISOString();
}
